import { useState } from "react";
import RightTimeApi from "@/API"; // Ensure you import RightTimeApi if it's used
import { MdCheckBoxOutlineBlank, MdCheckBox } from "react-icons/md";
import { CiEdit } from "react-icons/ci";
import { RiSave3Fill } from "react-icons/ri";
import { ImCancelCircle } from "react-icons/im";
import { CgEnter } from "react-icons/cg";
import SelectModal from "@/elements/modals/SelectModal";

export default function RecentsColumns(props) {
  const [historyEditingId, updateHistoryEditingId] = useState(0);
  const [historyColumnTitle, updateHistoryColumnTitle] = useState("");
  const [draggedRowId, setDraggedRowId] = useState(null);
  const [dropTargetRowId, setDropTargetRowId] = useState(null);
  const [dropPosition, setDropPosition] = useState(null);
  const [showColumnSetModal, setShowColumnSetModal] = useState(false);
  const [historyColumnSets, setHistoryColumnSets] = useState([]);

  if (!props || !props.selectedRole || !props.selectedRole.id) return null;

  async function toggleAllColumnsAsync() {
    const resp = await RightTimeApi.postAsync(
      "/RoleColumnsToggleAll?roleId=" + props.selectedRole.id
    );
    if (resp && resp.ok) {
      await props.updateData();
    }
  }

  async function saveHistoryEditAsync(e) {
    let data = {
      columnId: historyEditingId,
      roleId: props.selectedRole.id,
      title: historyColumnTitle,
    };
    const resp = await RightTimeApi.putAsync("/RoleColumnTitle", data).then(
      (resp) => {
        props.updateData();
      }
    );
    updateHistoryEditingId(0);
    updateHistoryColumnTitle("");
  }

  function handleDragStart(e, rowId) {
    setDraggedRowId(rowId);
    e.dataTransfer.effectAllowed = "move";
  }

  function handleDragOver(e, rowId) {
    e.preventDefault(); // Allow dropping
    const boundingRect = e.currentTarget.getBoundingClientRect();
    const offsetY = e.clientY - boundingRect.top;

    // Determine if the drop is above or below the row
    if (offsetY < boundingRect.height / 2) {
      setDropPosition("above");
    } else {
      setDropPosition("below");
    }
    setDropTargetRowId(rowId);
  }

  async function handleDrop(e) {
    e.preventDefault();
    if (draggedRowId !== null && dropTargetRowId !== null) {
      const postData = {
        droppedColumnId: draggedRowId,
        targetColumnId: dropTargetRowId,
        dir: dropPosition,
        companyId: props.selectedRole.companyId,
        roleId: props.selectedRole.id,
      };
      props.updateSpinner(true);
      const resp = await RightTimeApi.putAsync("/RoleColumnMove2", postData);
      if (resp && resp.ok)
        await props.updateData();
      props.updateSpinner(false);
    }
    setDraggedRowId(null);
    setDropTargetRowId(null);
    setDropPosition(null);
  }

  function handleDragEnd() {
    setDraggedRowId(null);
    setDropTargetRowId(null);
    setDropPosition(null);
  }

  async function toggleColumnAsync(e) {
    let id = parseInt(e.currentTarget.getAttribute("data-id"));
    let data = { columnId: id, roleId: props.selectedRole.id };
    const resp = await RightTimeApi.putAsync("/RoleColumnToggle", data);
    if (resp && resp.ok) await props.updateData();
  }

  function startHistoryEdit(e) {
    let id = parseInt(e.currentTarget.getAttribute("data-id"));
    updateHistoryEditingId(id);
    let title = props.data.roleHistoryColumns.find(
      (rhc) =>
        rhc.companyRoleId === props.selectedRole.id && rhc.historyColumnId === id
    );
    if (title) updateHistoryColumnTitle(title.columnTitle);
  }

  function cancelHistoryEdit(e) {
    updateHistoryEditingId(0);
    updateHistoryColumnTitle("");
  }
  function onTitleChange(e) {
    updateHistoryColumnTitle(e.target.value);
  }
  function handleTitleKeyPress(e) {
    if (e.key === "Enter") saveHistoryEditAsync(e);
  }

  async function openColumnSetModal() {
    props.updateSpinner(true);
    const resp = await RightTimeApi.getAsync("/HistoryColumnSets");
    props.updateSpinner(false);
    
    if (resp && resp.ok && resp.jsonData) {
      setHistoryColumnSets(resp.jsonData);
      setShowColumnSetModal(true);
    }
  }

  function closeColumnSetModal() {
    setShowColumnSetModal(false);
  }

  async function handleColumnSetSelection(columnSet) {
    if (columnSet && columnSet.id) {
      props.updateSpinner(true);
      const resp = await RightTimeApi.putAsync(`/CompanyRole/${props.selectedRole.id}/ColumnSet/${columnSet.id}`);
      props.updateSpinner(false);
      
      if (resp && resp.ok) {
        await props.updateData();
      }
    }
    setShowColumnSetModal(false);
  }
  
  if (props.selectedRole.permissions.includes(157) || 
      props.selectedRole.permissions.some(p => p.id === 157)) {
    return (
      <div className="tableWrapper factTable scrollable recentsColumnsTable">
        <table className="draggable">
          <caption>
            <span className="title">{props.selectedRole.name} - Recents Table Columns</span>
            <CgEnter 
              className="actionIcon enterIcon selectColumnSetIcon" 
              onClick={openColumnSetModal}
              title="Select Column Set"
              data-help-id="selectColumnSet"
              style={{ marginLeft: '10px', cursor: 'pointer' }}
            />
          </caption>
          <thead>
            <tr>
              <th className="actions">
                {props.data.roleHistoryColumns.length > 0 ? (
                  <MdCheckBoxOutlineBlank
                    className="checkbox unchecked actionIcon"
                    onClick={toggleAllColumnsAsync}
                  />
                ) : (
                  <MdCheckBox
                    className="checkbox checked actionIcon"
                    onClick={toggleAllColumnsAsync}
                  />
                )}
            </th>
            <th style={{ minWidth: "175px" }}>Column</th>
            <th style={{ minWidth: "175px" }}>Displayed Title</th>
            <th style={{ minWidth: "100px" }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {props?.data?.roleHistoryColumns
            ? props.data.roleHistoryColumns
              .filter((rhc) => rhc.companyRoleId === props.selectedRole.id)
              .sort((a, b) => a.order - b.order)
              .map((hc) => {
                return historyEditingId === hc.historyColumnId ? (
                  <tr key={"rhc" + hc.historyColumnId}>
                    <td
                      key={"historyColumns" + hc.historyColumnId}
                      className="actions"
                    >
                      <MdCheckBox
                        className="checkbox checked actionIcon"
                        data-id={hc.historyColumnId}
                        onClick={toggleColumnAsync}
                      />
                    </td>
                    <td className="left">
                      {props.data.historyColumns.find(
                        (r) => r.id === hc.historyColumnId
                      )?.name ?? ""}
                    </td>
                    <td>
                      <input
                        value={historyColumnTitle}
                        className="inlineEdit"
                        autoFocus
                        onChange={onTitleChange}
                        onKeyDown={handleTitleKeyPress}
                      />
                    </td>
                    <td className="actions">
                      <RiSave3Fill
                        className="save actionIcon"
                        data-id={hc.historyColumnId}
                        onClick={saveHistoryEditAsync}
                        title="Save Changes"
                      />
                      <ImCancelCircle
                        className="cancel actionIcon"
                        data-id={hc.historyColumnId}
                        onClick={cancelHistoryEdit}
                        title="Cancel Changes"
                      />
                    </td>
                  </tr>
                ) : (
                  <tr
                    key={"rhc" + hc.historyColumnId}
                    draggable
                    onDragStart={(e) => handleDragStart(e, hc.historyColumnId)}
                    onDragOver={(e) => handleDragOver(e, hc.historyColumnId)}
                    onDrop={handleDrop}
                    onDragEnd={handleDragEnd}
                    className={`${dropTargetRowId === hc.historyColumnId && dropPosition === "above"
                      ? "drop-above"
                      : ""
                      } ${dropTargetRowId === hc.historyColumnId && dropPosition === "below"
                        ? "drop-below"
                        : ""
                      }`}
                  >
                    <td
                      key={"historyColumns" + hc.historyColumnId}
                      className="actions"
                    >
                      <MdCheckBox
                        className="checkbox checked actionIcon"
                        data-id={hc.historyColumnId}
                        onClick={toggleColumnAsync}
                      />
                    </td>
                    <td className="left">{hc.name}</td>
                    <td className="left">{hc.columnTitle}</td>
                    <td className="actions">
                      {historyEditingId === 0 &&
                        props.data.roleHistoryColumns.find(
                          (r) => r.historyColumnId === hc.historyColumnId
                        ) ? (
                        <>
                          <CiEdit
                            className="actionIcon editIcon"
                            data-id={hc.historyColumnId}
                            data-role={hc.companyRoleId}
                            onClick={startHistoryEdit}
                            title="Edit"
                          />
                        </>
                      ) : null}
                    </td>
                  </tr>
        );
              })
            : null}
        {props.data.historyColumns
          ? props.data.historyColumns
            .filter(
              (hc) =>
                !props.data.roleHistoryColumns.find(
                  (rhc) =>
                    rhc.historyColumnId === hc.id &&
                    rhc.companyRoleId === props.selectedRole.id
                )
            )
            .map((hc) => {
              return (
                <tr key={"hc" + hc.id}>
                  <td key={"historyColumns" + hc.id} className="actions">
                    <MdCheckBoxOutlineBlank
                      className="checkbox unchecked actionIcon"
                      data-id={hc.id}
                      onClick={toggleColumnAsync}
                    />
                  </td>
                  <td className="left">{hc.name}</td>
                  <td>{hc.columnTitle}</td>
                  <td></td>
                </tr>
              );
            })
          : null}
      </tbody>
    </table>
    
    <SelectModal
      isOpen={showColumnSetModal}
      title="Select Column Set"
      message="Choose a column set to apply to this role:"
      items={historyColumnSets}
      onSelect={handleColumnSetSelection}
      onCancel={closeColumnSetModal}
      caption="Column Sets"
      modalClassName="recentsColumnSetModal"
      
    />
    </div>
    );
  }
  
  return null;
}

